import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, LogIn } from 'lucide-react';
import { supabase } from '../services/supabase';

// `sin-perfil` = hay sesión pero no se ha podido leer el rol. No es
// `no-session` (esa frase habla de un enlace caducado) ni `ready` (esa pinta
// un check verde y un botón a una puerta que puede ser la equivocada).
type Stage = 'loading' | 'ready' | 'no-session' | 'sin-perfil';

interface Profile {
  full_name: string;
  role: 'admin' | 'director' | 'monitor' | 'alumno' | string;
}

// §3c — Landing post-set-password (y, a futuro, post-confirm-signup si
// se cambia `site_url` a esta URL). Es ruta pública: la sesión la monta
// Supabase JS si el usuario llega con un token en el hash. Si no hay
// sesión, mostramos un fallback que invita a hacer login.

// Fallback de la rama de app (alumno/jugador): el deep link
// `vertexperformance://` no abre nada en un navegador de escritorio ni si la
// app no está instalada, así que ofrecemos también los enlaces a las tiendas.
// El de Google Play se deriva del applicationId (com.vertexperformance.app).
//
// El de App Store necesita el id NUMÉRICO de la ficha, que no se deriva del
// bundle id. El de aquí (6799236630) sale de consultar el endpoint de Apple por
// el `bundleIdentifier` de `app/app.json`, no de buscar la app a ojo:
//
//   https://itunes.apple.com/lookup?bundleId=com.vertexperformance.app
//
// Sin locale a propósito (`/app/id…` en vez de `/es/app/…`): Apple redirige a
// la tienda del país de quien pulsa, y el club acabará teniendo gente fuera de
// España.
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.vertexperformance.app';
const APP_STORE_URL = 'https://apps.apple.com/app/id6799236630';

export default function WelcomePage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('loading');
  const [profile, setProfile] = useState<Profile | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { data: sessData } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!sessData.session) {
        setStage('no-session');
        return;
      }
      const { data: row, error: errorPerfil } = await supabase
        .from('profiles')
        .select('full_name, role')
        .eq('id', sessData.session.user.id)
        .single();
      if (cancelled) return;
      // ⚠️ SIN EL ROL, ESTA PÁGINA MANDA A LA PUERTA EQUIVOCADA.
      //
      // Con el `error` descartado, `role` quedaba `undefined`,
      // `dashboardTarget(undefined)` devuelve el deep link
      // `vertexperformance://` y un admin o un entrenador que acaba de activar
      // su cuenta salía con el botón de la app móvil — que en el navegador no
      // tiene handler: botón muerto. Y con el check verde de «Cuenta activa»
      // encima, así que ni eso se lee como un fallo.
      if (errorPerfil) {
        setStage('sin-perfil');
        return;
      }
      setProfile((row as Profile | null) ?? null);
      setStage('ready');
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  function dashboardTarget(role: string | undefined): string {
    // El director es rol de PANEL (hereda is_admin()) → mismo dashboard web
    // que el admin. Sin esto caía en el else y recibía el deep link a la app,
    // que en el navegador no tiene handler (botón muerto).
    if (role === 'admin' || role === 'director') return '/dashboard';
    if (role === 'monitor') return '/monitor/dashboard';
    // alumno u otro role: la web admin no tiene panel — el alumno usa la app
    // móvil. Le damos el deep link como puerta principal.
    return 'vertexperformance://';
  }

  const role = profile?.role;
  const fullName = profile?.full_name;
  const firstName = fullName ? fullName.split(' ')[0] : null;
  const target = dashboardTarget(role);
  const isWeb = target.startsWith('/');

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/4 w-96 h-96 rounded-full bg-gold-primary/5 blur-3xl" />
        <div className="absolute -bottom-1/4 -left-1/4 w-96 h-96 rounded-full bg-gold-primary/3 blur-3xl" />
      </div>

      <div className="w-full max-w-md relative">
        <div className="text-center mb-10">
          <img src="/logo.png" alt="VERTEX Performance" className="h-24 mx-auto mb-4" />
        </div>

        <div className="card shadow-gold" data-testid="welcome-card">
          {stage === 'loading' && (
            <div className="flex items-center justify-center py-10">
              <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
            </div>
          )}

          {stage === 'no-session' && (
            <div data-testid="welcome-no-session">
              <h2 className="font-oswald text-xl uppercase tracking-wider text-white mb-2">
                Bienvenido a VERTEX
              </h2>
              <p className="text-text-secondary text-sm mb-6">
                Inicia sesión para acceder a tu portal. Si has llegado aquí desde un email de
                invitación, el enlace puede haber caducado — pide al administrador que lo reenvíe.
              </p>
              <button
                type="button"
                onClick={() => navigate('/login', { replace: true })}
                className="btn-gold w-full flex items-center justify-center gap-2"
                data-testid="welcome-go-login"
              >
                <LogIn size={16} /> Ir al login
              </button>
            </div>
          )}

          {stage === 'sin-perfil' && (
            <div data-testid="welcome-sin-perfil">
              <h2 className="font-oswald text-xl uppercase tracking-wider text-white mb-2">
                Tu cuenta está activa
              </h2>
              <p className="text-text-secondary text-sm mb-6">
                No se ha podido comprobar tu rol, así que no sabemos a qué portal mandarte. Si eres
                del club, entra por el login del panel; si eres alumno o familia, usa la app.
              </p>
              <button
                type="button"
                onClick={() => navigate('/login', { replace: true })}
                className="btn-gold w-full flex items-center justify-center gap-2"
                data-testid="welcome-sin-perfil-login"
              >
                <LogIn size={16} /> Ir al login del panel
              </button>
            </div>
          )}

          {stage === 'ready' && (
            <div data-testid="welcome-ready">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle size={20} className="text-success" />
                <p className="text-success text-sm font-medium">Cuenta activa</p>
              </div>
              <h2
                className="font-oswald text-2xl uppercase tracking-wider text-white mb-3"
                data-testid="welcome-greeting"
              >
                ¡Listo{firstName ? `, ${firstName}` : ''}!
              </h2>
              <p className="text-text-secondary text-sm mb-6">
                Tu cuenta ya está activa. Estos son los próximos pasos:
              </p>

              <ul className="space-y-2 mb-7 text-sm text-text-secondary">
                {role === 'monitor' && (
                  <>
                    <li className="flex gap-2">
                      <span className="text-gold-primary">•</span>
                      Revisa tu agenda del día y la lista de alumnos asignados.
                    </li>
                    <li className="flex gap-2">
                      <span className="text-gold-primary">•</span>
                      Completa tu perfil con teléfono y foto desde el menú lateral si te falta algo.
                    </li>
                  </>
                )}
                {(role === 'admin' || role === 'director') && (
                  <>
                    <li className="flex gap-2">
                      <span className="text-gold-primary">•</span>
                      Accede al dashboard para ver KPIs del club en tiempo real.
                    </li>
                    <li className="flex gap-2">
                      <span className="text-gold-primary">•</span>
                      Invita a entrenadores desde la sección Entrenadores cuando estés listo.
                    </li>
                  </>
                )}
                {role !== 'admin' && role !== 'director' && role !== 'monitor' && (
                  <>
                    <li className="flex gap-2">
                      <span className="text-gold-primary">•</span>
                      Abre la app móvil VERTEX Performance para ver tus sesiones y bonos.
                    </li>
                    <li className="flex gap-2">
                      <span className="text-gold-primary">•</span>
                      Si aún no la tienes instalada, busca "VERTEX Performance" en la tienda.
                    </li>
                  </>
                )}
              </ul>

              {isWeb ? (
                <button
                  type="button"
                  onClick={() => navigate(target, { replace: true })}
                  className="btn-gold w-full flex items-center justify-center gap-2"
                  data-testid="welcome-go-dashboard"
                >
                  Ir al dashboard <ArrowRight size={16} />
                </button>
              ) : (
                <div data-testid="welcome-open-app-wrap">
                  <a
                    href={target}
                    className="btn-gold w-full flex items-center justify-center gap-2"
                    data-testid="welcome-open-app"
                  >
                    Abrir la app <ArrowRight size={16} />
                  </a>
                  <p className="text-text-secondary text-xs text-center mt-4">
                    ¿No se abre la app? Instálala desde tu tienda:
                  </p>
                  <div className="flex items-center justify-center gap-4 mt-2">
                    <a
                      href={PLAY_STORE_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="text-gold-primary text-xs underline"
                      data-testid="welcome-store-android"
                    >
                      Google Play
                    </a>
                    {APP_STORE_URL && (
                      <a
                        href={APP_STORE_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="text-gold-primary text-xs underline"
                        data-testid="welcome-store-ios"
                      >
                        App Store
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="text-text-secondary text-xs text-center mt-4 font-inter">
          Break your limits!!!
        </p>
      </div>
    </div>
  );
}
